import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import { AIGenerate, BackArrow, CogWheelIcon, HelpIcon, ToolIcon } from "components/atoms/Icon";
import { Logo } from "components/atoms/Logo";
import ChatbotSidebar from "components/organisms/Chatbot/ChatbotSidebar/ChatbotSidebar";
import { useActiveCustomerAccount } from "hooks/useActiveCustomerAccount";
import { usePermissions } from "hooks/usePermissions";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserPermissions } from "utils/enums";
import { Paragraph } from "../../atoms/Typography";
import Tab from "./HeaderComponent/Tab";
import HeaderDropdownMenu from "./HeaderDropdownMenu";

interface HeaderNavBarProps {
    memberOrganizations: BriefCustomerAccount[];
    activeOrganizationName: string;
}

function HeaderNavBar({ memberOrganizations, activeOrganizationName }: HeaderNavBarProps) {
    const location = useLocation();
    const navigate = useNavigate();

    const { user } = useAuth0();
    const { hasPermission } = usePermissions();
    const { activeCustomerAccount } = useActiveCustomerAccount();
    const prohibitGenerativeAI = activeCustomerAccount?.prohibitGenerativeAi || false;

    const userEmail = user?.email;
    if (!userEmail) throw new Error(`User email is ${userEmail} when it should be defined`);

    const [isChatOpen, setChatOpen] = useState(false);

    useEffect(() => {
        setChatOpen(false);
    }, [location.pathname]);

    const isBuildingPage = location.pathname.includes("buildings/");

    if (isBuildingPage) {
        return (
            <>
                <div className="nav-bar">
                    <Paragraph className="tabs">
                        <button
                            className="back-button"
                            type="button"
                            onClick={() => {
                                if (location.pathname.includes("energy") || location.pathname.includes("measures")) {
                                    navigate(-1);
                                } else {
                                    navigate("/");
                                }
                            }}
                        >
                            <BackArrow color="var(--audette-white)" />

                            {location.pathname.includes("energy") || location.pathname.includes("measures")
                                ? "Back"
                                : "Back to portfolio"}
                        </button>
                    </Paragraph>
                    <div className="nav-bar__user-controls">
                        {hasPermission(UserPermissions.UseChatbotBuilding) && !prohibitGenerativeAI && (
                            <button
                                type="button"
                                onClick={() => setChatOpen(!isChatOpen)}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                }}
                            >
                                <AIGenerate color="white" />
                            </button>
                        )}

                        {hasPermission(UserPermissions.ReadUser) && (
                            <Tab title="Settings" route="/settings">
                                <CogWheelIcon />
                            </Tab>
                        )}

                        {hasPermission(UserPermissions.InAppHelp) && (
                            <Tab title="Help" route="/help/Index">
                                <HelpIcon color="var(--audette-white)" />
                            </Tab>
                        )}

                        <HeaderDropdownMenu
                            userEmail={userEmail}
                            customerAccounts={memberOrganizations}
                            activeOrganizationName={activeOrganizationName}
                        />
                    </div>
                </div>
                {!prohibitGenerativeAI && (
                    <ChatbotSidebar
                        sidebarOpen={isChatOpen}
                        handleClose={() => setChatOpen(false)}
                        title="Building AI Chat"
                        page="building"
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div className="nav-bar">
                <Logo />
                <div className={classNames("tabs", "pages-tabs")}>
                    <Tab title="Portfolio" route="/portfolio" extraClass="page-tab" />
                    <Tab title="Reports" route="/reports" extraClass="page-tab" />
                    <Tab title="Carbon Policies" route="/policies" extraClass="page-tab" />
                </div>
                <div className="nav-bar__user-controls">
                    {hasPermission(UserPermissions.UseInternalTools) && (
                        <Tab title="Sandbox" route="/sandbox">
                            <ToolIcon />
                        </Tab>
                    )}

                    {hasPermission(UserPermissions.ReadUser) && (
                        <Tab title="Settings" route="/settings">
                            <CogWheelIcon />
                        </Tab>
                    )}

                    {hasPermission(UserPermissions.InAppHelp) && (
                        <Tab title="Help" route="/help/Index">
                            <HelpIcon color="var(--audette-white)" />
                        </Tab>
                    )}

                    <HeaderDropdownMenu
                        userEmail={userEmail}
                        customerAccounts={memberOrganizations}
                        activeOrganizationName={activeOrganizationName}
                    />
                </div>
            </div>
            {!prohibitGenerativeAI && (
                <ChatbotSidebar
                    sidebarOpen={isChatOpen}
                    handleClose={() => setChatOpen(false)}
                    title="Portfolio AI Chat"
                    page="portfolio"
                />
            )}
        </>
    );
}

export default HeaderNavBar;
